import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	Grid,
	Typography,
	LinearProgress,
	Collapse,
	Tooltip
} from '@material-ui/core';

import LabeledSwitch from "./LabeledSwitch";
import AlertNotificationSchedule from "./AlertNotificationSchedule";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import Constants from '../utils/Constants';

const styles = makeStyles(theme => ({
	heading: {
		fontWeight: 600
	},

	subHeading: {
		color: Constants.COLORS.SECONDARY.GREY
	},

	iconHeading: {
		fontSize: "1.2rem",
		textAlign: "end",
		color: theme.palette.grey[600],
		paddingRight: theme.spacing(0.5)
	},

	labeledSwitch: {
		width: "100%",
		justifyContent: "flex-start"
	},

	sourceContainer: {
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(4)
	},

	sourceMedia: {
		paddingLeft: theme.spacing(4.5),
		paddingRight: theme.spacing(4),
		paddingTop: theme.spacing(1)
	},

	topicLabeledSwitch: {
		width: "65%",
		justifyContent: "flex-start"
	},

	topicLinearProgress: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(3)
	},

	topicHeading: {
		marginTop: theme.spacing(4)
	},

	scheduleHeading: {
		marginTop: theme.spacing(4),

		"& > span:last-child": {
			fontWeight: 600,
			paddingRight: theme.spacing(1)
		}
	},

	tooltip: {
		maxWidth: 130
	}
}));

function RealtimeMessagingSettings(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);

	let classes = styles(),
		notificationLabelGridSizing = { xs: 6, sm: 6, md: 6, lg: 6, xl: 6 },
		notificationSwitchGridSizing = { xs: 3, sm: 3, md: 3, lg: 3, xl: 3 },
		twoColGridSizing = { xs: 6, sm: 6, md: 6, lg: 6, xl: 6 },
		{ libraryTopics, rtNotifications, notificationConfig, mobileNumber,
			hasRealTimeNotification, formChangeHandler, alertDaysButtonToggled, settings, gotoProfile } = props,
		showSchedule = (notificationConfig.alert_schedule) ? true : false;

	return (
		<React.Fragment>
			{AuthorizedUser.isAdmin(authenticatedUser) ? (
				<React.Fragment>
					<Grid container direction="row" alignItems="center" spacing={2}>
						<Grid item>
							<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
						</Grid>
						<Grid item>
							<Typography className={classes.heading}>Escalation Notifications</Typography>
							<Typography  className={classes.subHeading}>You will receive an SMS when a team member escalates an alert.</Typography>
						</Grid>
					</Grid>
					<Grid
						container
						wrap="nowrap"
						justifyContent="flex-start"
						alignItems="center"
						className={classes.sourceContainer}
						style={{ marginBottom: '20px' }}>
						<React.Fragment>
							<Grid item >
								<LabeledSwitch
									reverse
									id="text_escalation"
									name="text_escalation"
									className={classes.labeledSwitch}
									checked={notificationConfig.text_escalation}
									onChange={formChangeHandler}
									aria-label="social media alert via text message"
								/>
							</Grid>
							<Grid item>
								<Typography>SMS</Typography>
							</Grid>
						</React.Fragment>
					</Grid>

				</React.Fragment>
			) : null}
			<Grid container direction="row" alignItems="center" spacing={2}>
				<Grid item>
					<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
				</Grid>
				<Grid item>
					<Typography className={classes.heading}>Notifications By Source</Typography>
					<Typography className={classes.subHeading}>You will receive an email or text message for sources you have enabled.</Typography>
				</Grid>
			</Grid>
			<Grid className={classes.sourceMedia}>
				<Typography className={classes.heading}>Social Media</Typography>
			</Grid>
			<Grid
				container
				wrap="nowrap"
				justifyContent="flex-start"
				alignItems="center"
				className={classes.sourceContainer}>
				{rtNotifications.includes("email_alerts") ? (
					<React.Fragment>
						<Grid item>
							<LabeledSwitch
								reverse
								id="email_alerts"
								name="email_alerts"
								className={classes.labeledSwitch}
								checked={notificationConfig.email_alerts}
								onChange={formChangeHandler}
								aria-label="social media alert via email"
							/>
						</Grid>
						<Grid item>
							<Typography>Email</Typography>
						</Grid>
					</React.Fragment>
				) : null}
			</Grid>
			<Grid
				container
				wrap="nowrap"
				justifyContent="flex-start"
				alignItems="center"
				className={classes.sourceContainer}
				style={{ marginBottom: '20px' }}>
				{rtNotifications.includes("text_alerts") ? (
					<React.Fragment>
						<Grid item >
							<LabeledSwitch
								reverse
								id="text_alerts"
								name="text_alerts"
								className={classes.labeledSwitch}
								checked={mobileNumber ? notificationConfig.text_alerts : false}
								disabled={!mobileNumber}
								onChange={formChangeHandler}
								aria-label="social media alert via text message"
							/>
						</Grid>
						<Grid item>
							<Typography>Text Message</Typography>
						</Grid>
					</React.Fragment>
				) : null}
			</Grid>
			{/* <Grid
				container
				wrap="nowrap"
				justify="flex-end"
				alignItems="center"
				className={classes.sourceContainer}>
				{rtNotifications.includes("email_gmail") ? (
					<React.Fragment>
						<Grid item {...notificationLabelGridSizing}>
							<Typography>G Suite</Typography>
						</Grid>
						<Grid item {...notificationSwitchGridSizing}>
							<LabeledSwitch
								reverse
								id="email_gmail"
								name="email_gmail"
								className={classes.labeledSwitch}
								checked={notificationConfig.email_gmail}
								onChange={formChangeHandler}
								aria-label="G Suite alert via email"
								label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />}
							/>
						</Grid>
					</React.Fragment>
				) : null }
				{rtNotifications.includes("text_gmail") ? (
					<Grid item {...notificationSwitchGridSizing}>
						<LabeledSwitch
							reverse
							id="text_gmail"
							name="text_gmail"
							className={classes.labeledSwitch}
							checked={mobileNumber ? notificationConfig.text_gmail : false}
							disabled={!mobileNumber}
							onChange={formChangeHandler}
							aria-label="G Suite alert via text message"
							label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "comment"]} />}
						/>
					</Grid>
				) : null}
			</Grid>
			<Grid
				container
				wrap="nowrap"
				justify="flex-end"
				alignItems="center"
				className={classes.sourceContainer}>
				{rtNotifications.includes("email_exchange") ? (
					<React.Fragment>
						<Grid item {...notificationLabelGridSizing}>
							<Typography>Outlook</Typography>
						</Grid>
						<Grid item {...notificationSwitchGridSizing}>
							<LabeledSwitch
								reverse
								id="email_exchange"
								name="email_exchange"
								className={classes.labeledSwitch}
								checked={notificationConfig.email_exchange}
								onChange={formChangeHandler}
								aria-label="outlook alert via email"
								label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />}
							/>
						</Grid>
					</React.Fragment>
				) : null}
				{rtNotifications.includes("text_exchange") ? (
					<Grid item {...notificationSwitchGridSizing}>
						<LabeledSwitch
							reverse
							id="text_exchange"
							name="text_exchange"
							className={classes.labeledSwitch}
							checked={mobileNumber ? notificationConfig.text_exchange : false}
							disabled={!mobileNumber}
							onChange={formChangeHandler}
							aria-label="outlook alert via text message"
							label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "comment"]} />}
						/>
					</Grid>
				) : null}
			</Grid>
			<Grid
				container
				wrap="nowrap"
				justify="flex-end"
				alignItems="center"
				className={classes.sourceContainer}>
				{rtNotifications.includes("email_shareit") ? (
					<React.Fragment>
						<Grid item {...notificationLabelGridSizing}>
							<Typography>Shareit</Typography>
						</Grid>
						<Grid item {...notificationSwitchGridSizing}>
							<LabeledSwitch
								reverse
								id="email_shareit"
								name="email_shareit"
								className={classes.labeledSwitch}
								checked={notificationConfig.email_shareit}
								onChange={formChangeHandler}
								aria-label="share it alert via email"
								label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />}
							/>
						</Grid>
					</React.Fragment>
				) : null}
				{rtNotifications.includes("text_shareit") ? (
					<Grid item {...notificationSwitchGridSizing}>
						<LabeledSwitch
							reverse
							id="text_shareit"
							name="text_shareit"
							className={classes.labeledSwitch}
							checked={mobileNumber ? notificationConfig.text_shareit : false}
							disabled={!mobileNumber}
							onChange={formChangeHandler}
							aria-label="share it alert via text message"
							label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "comment"]} />}
						/>
					</Grid>
				) : null}
			</Grid> */}

			{/* <Grid
				container
				wrap="nowrap"
				justify="flex-end"
				alignItems="center"
				className={classes.sourceContainer}>
				{rtNotifications.includes("email_web_search") ? (
					<React.Fragment>
						<Grid item {...notificationLabelGridSizing}>
							<Typography>Filter Web Search</Typography>
						</Grid>
						<Grid item {...notificationSwitchGridSizing}>
							<LabeledSwitch
								reverse
								id="email_web_search"
								name="email_web_search"
								className={classes.labeledSwitch}
								checked={notificationConfig.email_web_search}
								onChange={formChangeHandler}
								aria-label="web search alert via email"
								label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />}
							/>
						</Grid>
					</React.Fragment>
				) : null}
				{rtNotifications.includes("text_web_search") ? (
					<Grid item {...notificationSwitchGridSizing}>
						<LabeledSwitch
							reverse
							id="text_web_search"
							name="text_web_search"
							className={classes.labeledSwitch}
							checked={mobileNumber ? notificationConfig.text_web_search : false}
							disabled={!mobileNumber}
							onChange={formChangeHandler}
							aria-label="web search alert via text message"
							label={<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "comment"]} />}
						/>
					</Grid>
				) : null}
			</Grid> */}

			<Grid container direction="row" alignItems="center" spacing={2}>
				<Grid item>
					<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
				</Grid>
				<Grid item>
					<Typography className={`${classes.heading}`}>Notifications By Topic</Typography>
					<Typography  className={classes.subHeading}>You will receive an email or text message for topics you have enabled, based on what you’ve selected above.</Typography>
				</Grid>
			</Grid>
			<Grid container>
				{libraryTopics && libraryTopics.length ? (
					<React.Fragment>
						<Grid
							container
							wrap="nowrap"
							justifyContent="flex-start"
							alignItems="center"
							className={classes.sourceContainer}>
							<Grid item>
								{libraryTopics.map((item, idx) => {
									return idx <= 3 ? (
										<React.Fragment>
											<Tooltip
												key={idx}
												placement="top"
												disableFocusListener
												title={item.description}
												classes={{ tooltip: classes.tooltip }}>
												<LabeledSwitch
													id={item.id}
													name={`${item.id}`} // FormControlLabel expects a string, doing this to simplify / re-use stuff in container
													label={item.name}
													labelPlacement="end"
													aria-label={`${item.name}, ${item.description}`}
													className={classes.topicLabeledSwitch}
													onChange={(e) => formChangeHandler(e, { topic: item })}
													disabled={notificationConfig.activeLibraryTopics.length === 0 && !hasRealTimeNotification}
													checked={notificationConfig.activeLibraryTopics.find(topic => topic.id === item.id) != null} />
											</Tooltip>
										</React.Fragment>
									) : null;
								})}
							</Grid>
						</Grid>
						<Grid
							container
							wrap="nowrap"
							justifyContent="flex-start"
							alignItems="center"
							className={classes.sourceContainer}>
							<Grid item>
								{libraryTopics.map((item, idx) => {
									return idx > 3 ? (
										<Tooltip
											key={idx}
											placement="top"
											disableFocusListener
											title={item.description}
											classes={{ tooltip: classes.tooltip }}>
											<LabeledSwitch
												id={item.id}
												name={`${item.id}`} // FormControlLabel expects a string, doing this to simplify / re-use stuff in container
												label={item.name}
												labelPlacement="end"
												aria-label={`${item.name}, ${item.description}`}
												className={classes.topicLabeledSwitch}
												onChange={(e) => formChangeHandler(e, { topic: item })}
												disabled={notificationConfig.activeLibraryTopics.length === 0 && !hasRealTimeNotification}
												checked={notificationConfig.activeLibraryTopics.find(topic => topic.id === item.id) != null} />
										</Tooltip>
									) : null;
								})}
							</Grid>
						</Grid>
					</React.Fragment>
				) : <Grid item style={{ width: "100%" }}><LinearProgress className={classes.topicLinearProgress} /></Grid>}
			</Grid>

			{AuthorizedUser.hasDiscussions(authenticatedUser) ?
				<React.Fragment>
					<br />
					<Grid container direction="row" alignItems="center" spacing={2} wrap="nowrap">
						<Grid item>
							<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
						</Grid>
						<Grid item>
							<Typography className={classes.heading}>Notifications For Trending Topics</Typography>
							<Typography  className={classes.subHeading}>You will receive an email or text message when a safety and security discussion topic is trending on social media, based on what you’ve selected above. These notifications will follow the same schedule you have configured below in the Notification Schedule section.</Typography>
						</Grid>
					</Grid>
					<Grid className={classes.sourceMedia}>
						<Typography className={classes.heading}>Trending Discussion Topics</Typography>
					</Grid>
					<Grid
						container
						wrap="nowrap"
						justifyContent="flex-start"
						alignItems="center"
						className={classes.sourceContainer}>
						<Grid item>
							<LabeledSwitch
								reverse
								id="email_trending_topics"
								name="email_trending_topics"
								className={classes.labeledSwitch}
								checked={notificationConfig.email_trending_topics}
								onChange={formChangeHandler}
								aria-label="trending topic notification via email"
							/>
						</Grid>
						<Grid item>
							<Typography>Email</Typography>
						</Grid>
					</Grid>
					<Grid
						container
						wrap="nowrap"
						justifyContent="flex-start"
						alignItems="center"
						className={classes.sourceContainer}>
						<Grid item>
							<LabeledSwitch
								reverse
								id="text_trending_topics"
								name="text_trending_topics"
								className={classes.labeledSwitch}
								checked={mobileNumber ? notificationConfig.text_trending_topics : false}
								disabled={!mobileNumber}
								onChange={formChangeHandler}
								aria-label="trending topic notification via text message"
							/>
						</Grid>
						<Grid item>
							<Typography>Text Message</Typography>
						</Grid>
					</Grid>
				</React.Fragment>
				: null
			}

			<React.Fragment>
				<br />
				<Grid container direction="row" alignItems="center" spacing={2}>
					<Grid item>
						<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
					</Grid>
					<Grid item>
						<Typography className={`${classes.heading}`}>Notifications For Detected Patterns</Typography>
						<Typography className={classes.subHeading}>You will receive an email or text message when a pattern is detected. These notifications will follow the same schedule you have configured below in the Notification Schedule section.</Typography>
					</Grid>
				</Grid>
				<Grid className={classes.sourceMedia}>
					<Typography className={classes.heading}>Detected Patterns</Typography>
				</Grid>
				<Grid
					container
					wrap="nowrap"
					justifyContent="flex-start"
					alignItems="center"
					className={classes.sourceContainer}>
					<Grid item>
						<LabeledSwitch
							reverse
							id="email_patterns"
							name="email_patterns"
							className={classes.labeledSwitch}
							checked={notificationConfig.email_patterns}
							onChange={formChangeHandler}
							aria-label="patterns via email"
						/>
					</Grid>
					<Grid item>
						<Typography>Email</Typography>
					</Grid>
				</Grid>
				<Grid
					container
					wrap="nowrap"
					justifyContent="flex-start"
					alignItems="center"
					className={classes.sourceContainer}>
					<Grid item>
						<LabeledSwitch
							reverse
							id="text_patterns"
							name="text_patterns"
							className={classes.labeledSwitch}
							checked={mobileNumber ? notificationConfig.text_patterns : false}
							disabled={!mobileNumber}
							onChange={formChangeHandler}
							aria-label="patterns via text message"
						/>
					</Grid>
					<Grid item>
						<Typography>Text Message</Typography>
					</Grid>
				</Grid>
			</React.Fragment>

			<LabeledSwitch
				reverse
				id="alert_schedule"
				name="alert_schedule"
				label="Notification Schedule"
				checked={notificationConfig.alert_schedule}
				onChange={formChangeHandler}
				disabled={!hasRealTimeNotification}
				className={`${classes.heading} ${classes.scheduleHeading}`} />
			<Typography>Set a delivery schedule for your alerts. If a schedule is not set you will receive alert notifications 24 hours, 7 days a week.</Typography>
			<Collapse in={showSchedule}>
				<AlertNotificationSchedule
					settings={settings}
					notificationConfig={notificationConfig}
					formChangeHandler={formChangeHandler}
					alertDaysButtonToggled={alertDaysButtonToggled}
					gotoProfile={gotoProfile} />
			</Collapse>
		</React.Fragment>
	);
}

RealtimeMessagingSettings.propTypes = {
	settings: PropTypes.object,
	mobileNumber: PropTypes.string,
	libraryTopics: PropTypes.array,
	rtNotifications: PropTypes.array,
	notificationConfig: PropTypes.object,
	hasRealTimeNotification: PropTypes.bool,
	formChangeHandler: PropTypes.func.isRequired,
	alertDaysButtonToggled: PropTypes.func.isRequired,
	gotoProfile: PropTypes.func.isRequired
};

export default RealtimeMessagingSettings;